import {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import Button from '@mui/material/Button'
import Modal from '../../../components/common/Modal/ModalComponent'
import {StyledCancelButton} from '../../AdminPanelStyling'
import {useUpdateClientName} from '../../api/updateClientName'
import {useGetClientDetails} from '../../api/getClientDetails'
import {StyledModalActions, StyledModalContent} from './ClientNameEditModalStyles'
import {validateClientName} from '../Helpers/formValidation'
import ValidationRules from '../../ManageClientPermissions/ValidationRules'
import PopupComponent from '../../ProjectDetails/PopupComponent/PopupComponent'
import TextFieldWithCounter from '../TextFieldWithCounter/TextFieldWithCounter'
import InlineErrors from '../Errors/InlineErrors.json'

const ClientNameEditModal = ({open, onClose, clientId, clientName, onSave}) => {
  const [editedClientName, setEditedClientName] = useState('')
  const [clientNameError, setClientNameError] = useState('')
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false)
  const [updateClient, {loading: updatingClient}] = useUpdateClientName()
  const {clientData} = useGetClientDetails()

  useEffect(() => {
    if (!open) return
    setEditedClientName(clientName || '')
    setClientNameError('')
  }, [clientName, open])

  const hasClientNameChanged = editedClientName.trim() !== (clientName || '').trim()

  const isDuplicateClientName = (nameToCheck) => {
    const normalizedName = (nameToCheck || '').trim()
    if (!normalizedName) {
      return false
    }

    const selectedClientId = Number.parseInt(clientId, 10)
    return (clientData?.clientList || []).some((client) => {
      const existingName = (client?.CLIENT_NAME || '').trim()
      const existingClientId = Number.parseInt(client?.CLIENT_ID, 10)
      return existingName === normalizedName && existingClientId !== selectedClientId
    })
  }

  const handleClose = () => {
    setEditedClientName('')
    setClientNameError('')
    onClose()
  }

  const requestClose = () => {
    if (!hasClientNameChanged) {
      handleClose()
      return
    }
    setShowCloseConfirmation(true)
  }

  const handleCloseConfirmationChange = (isOpen) => {
    setShowCloseConfirmation(isOpen)
  }

  const handleConfirmClose = () => {
    handleCloseConfirmationChange(false)
    handleClose()
  }

  const handleClientNameChange = (event) => {
    const value = event.target.value
    setEditedClientName(value)

    const validationError = validateClientName(value, 50)
    if (validationError) {
      setClientNameError(validationError)
      return
    }

    if (isDuplicateClientName(value)) {
      setClientNameError(InlineErrors.duplicateName)
      return
    }

    setClientNameError('')
  }

  const handleSaveClientName = async () => {
    const validationError = validateClientName(editedClientName, 50)
    if (validationError) {
      setClientNameError(validationError)
      return
    }

    if (isDuplicateClientName(editedClientName)) {
      setClientNameError(InlineErrors.duplicateName)
      return
    }

    try {
      const result = await updateClient({
        variables: {
          clientId: Number.parseInt(clientId, 10),
          clientName: editedClientName.trim()
        }
      })

      if (result?.data?.updateClient?.updatedClientDetails) {
        handleClose()
        if (onSave) {
          await onSave()
        }
      } else {
        setClientNameError('Failed to update client name. Please try again.')
      }
    } catch (error) {
      console.error('Error updating client name:', error)
      setClientNameError('An error occurred while updating. Please try again.')
    }
  }

  return (
    <Modal
      open={open}
      onClose={requestClose}
      title='Edit Client Name'
      aria={{labelledBy: 'edit-client-name-title'}}
      maxWidth='sm'
      disableBackdropClick
    >
      <StyledModalContent>
        <TextFieldWithCounter
          value={editedClientName}
          onChange={handleClientNameChange}
          label='Client Name'
          maxLength={50}
          enforceMaxLength={false}
          fullWidth
          size='medium'
          error={!!clientNameError}
          helperText={clientNameError}
        />
        <ValidationRules refuseNumbers refuseSpecialCharacters />
        <StyledModalActions>
          <StyledCancelButton variant='text' onClick={requestClose}>
            Cancel
          </StyledCancelButton>
          <Button
            variant='contained'
            onClick={handleSaveClientName}
            disabled={
              !editedClientName.trim() ||
              !!clientNameError ||
              updatingClient ||
              !hasClientNameChanged ||
              isDuplicateClientName(editedClientName)
            }
          >
            {updatingClient ? 'Saving Client...' : 'Save Client'}
          </Button>
        </StyledModalActions>
      </StyledModalContent>
      {showCloseConfirmation && (
        <PopupComponent
          open={showCloseConfirmation}
          setOpen={handleCloseConfirmationChange}
          popupStyle='cancel'
          setGoBack={handleConfirmClose}
          isCompleted={true}
        />
      )}
    </Modal>
  )
}

ClientNameEditModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  clientName: PropTypes.string,
  onSave: PropTypes.func
}

ClientNameEditModal.defaultProps = {
  clientName: '',
  onSave: undefined
}

export default ClientNameEditModal
